import React, { useEffect, useState } from 'react';
import { Descriptions, Drawer, Space, Spin, Tag, Typography } from 'antd';
import api from '../api/client';
import { Vehicle } from '../api/vehicles';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  vehicleId: number | null;
  width?: number;
  extra?: React.ReactNode;
  refreshKey?: number;
  onOwnerClick?: (ownerType: 'individual' | 'counterparty', ownerId: number) => void;
}

const VehicleInfoDrawer: React.FC<Props> = ({
  open, onClose, vehicleId, width = 460, extra, refreshKey, onOwnerClick,
}) => {
  const [data, setData] = useState<Vehicle | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !vehicleId) return;
    setLoading(true);
    setData(null);
    api.get(`/vehicles/${vehicleId}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, vehicleId, refreshKey]);

  const title = data ? `${data.make} ${data.model}${data.year ? ` (${data.year})` : ''}` : 'Автомобиль';
  const ownerLabel = data?.owner_type === 'individual' ? 'Физлицо' : data?.owner_type === 'counterparty' ? 'Юрлицо' : null;

  return (
    <Drawer title={title} open={open} onClose={onClose} width={width} extra={extra}>
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : data && (
        <>
          <Space wrap style={{ marginBottom: 16 }}>
            {data.is_active
              ? <Tag color="green">Активен</Tag>
              : <Tag color="default">Архив</Tag>}
            {ownerLabel && <Tag color={data.owner_type === 'individual' ? 'blue' : 'purple'}>{ownerLabel}</Tag>}
          </Space>

          <Descriptions column={1} size="small" bordered style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Марка">{data.make}</Descriptions.Item>
            <Descriptions.Item label="Модель">{data.model}</Descriptions.Item>
            {data.year && <Descriptions.Item label="Год выпуска">{data.year}</Descriptions.Item>}
            {data.license_plate && (
              <Descriptions.Item label="Гос. номер">{data.license_plate}</Descriptions.Item>
            )}
            {data.vin && <Descriptions.Item label="VIN">{data.vin}</Descriptions.Item>}
            {data.estimated_value != null && (
              <Descriptions.Item label="Оценочная стоимость">
                {Number(data.estimated_value).toLocaleString('ru-RU')} ₽
              </Descriptions.Item>
            )}
            {data.owner_name && data.owner_type && data.owner_id ? (
              <Descriptions.Item label="Владелец">
                {onOwnerClick ? (
                  <a
                    onClick={(e) => {
                      e.preventDefault();
                      onOwnerClick(data.owner_type!, data.owner_id!);
                    }}
                    href="#"
                  >
                    {data.owner_name}
                  </a>
                ) : (
                  data.owner_name
                )}
              </Descriptions.Item>
            ) : (
              <Descriptions.Item label="Владелец">
                <span style={{ color: '#999' }}>не указан</span>
              </Descriptions.Item>
            )}
          </Descriptions>

          {data.notes && (
            <div>
              <Text strong>Примечание</Text>
              <div style={{ marginTop: 8, color: '#555' }}>{data.notes}</div>
            </div>
          )}
        </>
      )}
    </Drawer>
  );
};

export default VehicleInfoDrawer;
